import React, { useState, useEffect, useCallback } from "react";
import "../../css/manage_waba.css";
import { apiCall } from "../../services/authServieces";
import ShowSnackBar from "../../components/snackBar";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import SearchIcon from "@mui/icons-material/Search";
import Checkbox from "@mui/material/Checkbox";
import { FaSave } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import Edit from "../../Assets/images/edit.png";
import { ClipLoader } from "react-spinners";

const UserRights = () => {
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [snackBar, setSnackBar] = useState({
        open: false,
        severity: "success",
        message: "",
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [editMode, setEditMode] = useState(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [originalRowData, setOriginalRowData] = useState(null);

    const columns = [
        { id: "superadmin_id", label: "Super Admin ID", minWidth: 120 },
        { id: "superadmin_name", label: "Name", minWidth: 150 },
        { id: "dealers", label: "Dealers", minWidth: 100 },
        { id: "transactions", label: "Transactions", minWidth: 100 },
        { id: "reports", label: "Reports", minWidth: 100 },
        { id: "actions", label: "Actions", minWidth: 120 },
    ];

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    const fetchSuperAdminData = useCallback(async () => {
        setLoading(true);
        try {
            let endpoint = `api/user-rights/get-superadmin-userrights-data?page=${page}&limit=${rowsPerPage}`;
            
            if (debouncedSearchTerm) {
                endpoint = `${endpoint}&search=${debouncedSearchTerm}`;
            }

            const response = await apiCall({ method: "GET", endpoint: endpoint });

            if (response.success) {
                setRows(response.data || []);
                setTotal(response.total || 0);
            } else {
                setSnackBar({
                    open: true,
                    severity: "error",
                    message: "Failed to fetch user rights data",
                });
                setRows([]);
                setTotal(0);
            }
        } catch (error) {
            console.error("API Error:", error);
            setSnackBar({
                open: true,
                severity: "error",
                message: "Error occurred while fetching user rights data",
            });
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, debouncedSearchTerm]);

    const updateUserRights = useCallback(async (superadminId, updatedData) => {
        setLoading(true);
        try {
            const endpoint = `api/user-rights/update-user-rights/${superadminId}`;
            
            const response = await apiCall({ 
                method: "PUT", 
                endpoint: endpoint,
                payload: updatedData
            });

            if (response.success) {
                setSnackBar({
                    open: true,
                    severity: "success",
                    message: "User rights updated successfully!",
                });
                // Refresh data to get latest state
                fetchSuperAdminData();
                return true;
            } else {
                setSnackBar({
                    open: true,
                    severity: "error",
                    message: response.message || "Failed to update user rights",
                });
                return false;
            }
        } catch (error) {
            console.error("Update API Error:", error);
            setSnackBar({
                open: true,
                severity: "error",
                message: "Error occurred while updating user rights",
            });
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchSuperAdminData]);

    // Fetch data when dependencies change
    useEffect(() => {
        fetchSuperAdminData();
    }, [fetchSuperAdminData]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleCloseSnackBar = (event, reason) => {
        setSnackBar((prevState) => ({ ...prevState, open: false }));
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(0); // Reset to first page when searching
    };

    const handleEdit = (row) => {
        setEditMode(row.superadmin_id);
        // Store original data for cancel functionality
        setOriginalRowData({ ...row });
    };

    const handleSave = async (row) => {
        const updatedData = {
            dealers_read: row.dealers_read,
            dealers_write: row.dealers_write,
            transactions_read: row.transactions_read,
            transactions_write: row.transactions_write,
            reports_read: row.reports_read,
        };

        const success = await updateUserRights(row.superadmin_id, updatedData);
        if (success) {
            setEditMode(null);
            setOriginalRowData(null);
        }
    };

    const handleCancel = () => {
        if (originalRowData) {
            // Restore original data
            const updatedRows = rows.map(row => {
                if (row.superadmin_id === originalRowData.superadmin_id) {
                    return { ...originalRowData };
                }
                return row;
            });
            setRows(updatedRows);
        }
        setEditMode(null);
        setOriginalRowData(null);
    };

    const handleCheckboxChange = (superadminId, field) => {
        const updatedRows = rows.map(row => {
            if (row.superadmin_id === superadminId) {
                return { ...row, [field]: !row[field] };
            }
            return row;
        });
        setRows(updatedRows);
    };

    const getCurrentDateInIST = () => {
        const now = new Date();
        const options = {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        };
        return new Intl.DateTimeFormat('en-IN', options).format(now);
    };

    return (
        <>
            <div className="Template_id_contian">
                <h4 className="Head_title AdminSearchContain user_right user_right1">
                    {/* <div className="TodayDeliveryRepot">
                        {getCurrentDateInIST()}
                    </div> */}
                    User Rights Management
                    
                    <div className="Session_report_SearchContain userRights_search">
                        <input
                            placeholder="Search"
                            type="text"
                            className=""
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        {/* <button type="button">
                            <SearchIcon />
                        </button> */}
                    </div>
                </h4>

                <Paper sx={{ width: "100%", overflow: "hidden" }}>
                    <TableContainer sx={{ maxHeight: 440 }}>
                        <Table stickyHeader aria-label="sticky table" className="Table ColorBlueTable DailyReportTable">
                            <TableHead>
                                <TableRow>
                                    <TableCell rowSpan={2}>{columns[0].label}</TableCell>
                                    <TableCell rowSpan={2}>{columns[1].label}</TableCell>
                                    <TableCell colSpan={2} align="center">{columns[2].label}</TableCell>
                                    <TableCell colSpan={2} align="center">{columns[3].label}</TableCell>
                                    <TableCell rowSpan={2} align="center">{columns[4].label}</TableCell>
                                    <TableCell rowSpan={2}>{columns[5].label}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align="center">Read</TableCell>
                                    <TableCell align="center">Write</TableCell>
                                    <TableCell align="center">Read</TableCell>
                                    <TableCell align="center">Write</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length > 0 ? (
                                    rows.map((row) => (
                                        <TableRow hover tabIndex={-1} key={row.superadmin_id}>
                                            <TableCell>{row.superadmin_id}</TableCell>
                                            <TableCell>{row.superadmin_name || 'N/A'}</TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={row.dealers_read || false}
                                                    onChange={() => handleCheckboxChange(row.superadmin_id, "dealers_read")}
                                                    disabled={editMode !== row.superadmin_id}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={row.dealers_write || false}
                                                    onChange={() => handleCheckboxChange(row.superadmin_id, "dealers_write")}
                                                    disabled={editMode !== row.superadmin_id}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={row.transactions_read || false}
                                                    onChange={() => handleCheckboxChange(row.superadmin_id, "transactions_read")}
                                                    disabled={editMode !== row.superadmin_id}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={row.transactions_write || false}
                                                    onChange={() => handleCheckboxChange(row.superadmin_id, "transactions_write")}
                                                    disabled={editMode !== row.superadmin_id}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={row.reports_read || false}
                                                    onChange={() => handleCheckboxChange(row.superadmin_id, "reports_read")}
                                                    disabled={editMode !== row.superadmin_id}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {editMode === row.superadmin_id ? (
                                                    <>
                                                        <FaSave 
                                                            style={{ 
                                                                marginRight: "15px", 
                                                                cursor: "pointer", 
                                                                fontSize: "20px",
                                                                color: "green"
                                                            }} 
                                                            onClick={() => handleSave(row)} 
                                                        />
                                                        <MdCancel 
                                                            style={{ 
                                                                cursor: "pointer", 
                                                                fontSize: "20px", 
                                                                color: "red" 
                                                            }} 
                                                            onClick={handleCancel} 
                                                        />
                                                    </>
                                                ) : (
                                                    <img 
                                                        src={Edit} 
                                                        alt="Edit" 
                                                        className="edit_btn_icon" 
                                                        onClick={() => handleEdit(row)}
                                                        style={{ cursor: "pointer" }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    !loading && (
                                        <TableRow>
                                            <TableCell colSpan={columns.length+2} align="center">
                                                No data available
                                            </TableCell>
                                        </TableRow> 
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </div>

            {loading && (
                <div className="spinnerOverlay">
                    <ClipLoader loading={loading} size={50} />
                </div>
            )}

            <ShowSnackBar
                open={snackBar.open}
                severity={snackBar.severity}
                message={snackBar.message}
                onClose={handleCloseSnackBar}
            />
        </>
    );
};

export default UserRights;